import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { alpha, useTheme } from '@mui/material/styles';
import { reviewBooksAPI } from '../services/api';
import { trackEvent } from '../utils/analytics';

const cyan = '#00BFFF';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

function resolveCoverUrl(coverImage) {
    if (!coverImage) return null;
    if (coverImage.startsWith('http://') || coverImage.startsWith('https://')) return coverImage;
    // Only server-uploaded files need the API origin; other paths (e.g. /books/...)
    // are static assets served by the client itself.
    if (coverImage.startsWith('/uploads/')) return `${SERVER_BASE_URL}${coverImage}`;
    return coverImage;
}

const FORMAT_LABELS = {
    ebook: 'eBook',
    paperback: 'Paperback',
    hardcover: 'Hardcover',
};

const Review = () => {
    const theme = useTheme();
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchBooks = async () => {
            try {
                const data = await reviewBooksAPI.getAll();
                setBooks(data.books || []);
            } catch (err) {
                setError('Failed to load review books. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchBooks();
    }, []);

    const handleReviewClick = (book, formatEntry) => {
        trackEvent('review_page_click', {
            book_title: book.title,
            format: formatEntry.format,
        });
    };

    return (
        <>
            <Helmet>
                <title>Leave a Review — ReaderFLEX</title>
                <meta name="description" content="Leave an honest review for ReaderFLEX books on Amazon. Your feedback helps other readers discover the right books." />
                <link rel="canonical" href="https://readerflex.com/review" />
            </Helmet>

            <Box sx={{ minHeight: '60vh', py: { xs: 4, md: 8 } }}>
                <Container maxWidth="md">
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <RateReviewIcon sx={{ fontSize: 36, color: cyan }} />
                            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                                Leave a Review
                            </Typography>
                        </Box>
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}>
                            Honest reviews help other readers decide whether a book is right for them.
                            Pick the format you purchased and share your experience on Amazon.
                        </Typography>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : books.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <MenuBookIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No books available for review yet.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Check back soon — we're adding more titles.
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={4} justifyContent="center">
                            {books.map((book) => (
                                <Grid item xs={12} sm={6} md={5} key={book._id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 4,
                                            border: `1px solid ${alpha(cyan, theme.palette.mode === 'dark' ? 0.3 : 0.2)}`,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {/* Book Cover */}
                                        {book.coverImage ? (
                                            <Box
                                                component="img"
                                                src={resolveCoverUrl(book.coverImage)}
                                                alt={`Cover of ${book.title}`}
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 200,
                                                    aspectRatio: '2 / 3',
                                                    objectFit: 'cover',
                                                    borderRadius: 2,
                                                    mb: 2,
                                                    boxShadow: `0 8px 30px ${alpha('#000', 0.3)}`,
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 200,
                                                    aspectRatio: '2 / 3',
                                                    borderRadius: 2,
                                                    mb: 2,
                                                    backgroundColor: alpha(cyan, 0.1),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: `1px solid ${alpha(cyan, 0.2)}`,
                                                }}
                                            >
                                                <MenuBookIcon sx={{ fontSize: 48, color: alpha(cyan, 0.5) }} />
                                            </Box>
                                        )}

                                        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 2 }}>
                                            {book.title}
                                        </Typography>

                                        {/* Format Buttons */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 280 }}>
                                            {book.formats.map((f) => (
                                                <Button
                                                    key={f.slug}
                                                    component={RouterLink}
                                                    to={`/review/${f.slug}`}
                                                    variant="outlined"
                                                    color="primary"
                                                    fullWidth
                                                    endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                                    onClick={() => handleReviewClick(book, f)}
                                                    sx={{ fontWeight: 600, textTransform: 'none' }}
                                                >
                                                    Review {FORMAT_LABELS[f.format] || f.format}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {/* Footer note */}
                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            Reviews are submitted on Amazon.com. You'll be redirected to the review page for the specific format you purchased.
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/books"
                            variant="text"
                            color="primary"
                            sx={{ mt: 2 }}
                        >
                            Browse all books
                        </Button>
                    </Box>
                </Container>
            </Box>
        </>
    );
};

export default Review;
